import { useEffect, useRef, useState } from "react";
import styles from "../Confirmation.module.scss";
import QuoteCard from "../QuoteCard/QuoteCard";
import {
  PrimaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/v2/Message";
import Close24PxOutlinedIcon from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/Close24PxOutlined";
import { paymentContent } from "../../PaymentOptions/tradeInCopyText";
import { TradeInError } from "../Confirmation";
import { QuotesResponse } from "../../PaymentOptions/PaymentOptions";
import NextStep, { TradeInSteps } from "../../NextStep/NextStep";
import { dispatchAnalyticsEvent } from "../../../../../../utils";

const ALERT_EXISTING: TradeInError = {
  title: "You already have a quote",
  body: "To add a new one, please remove the current quote from your basket",
  type: "information",
};

const ALERT_MULTIPLE: TradeInError = {
  title: "You have multiple quotes",
  body: "Make sure you have only one quote to continue your trade-in",
  type: "error",
};

const ALERT_EXPIRED: TradeInError = {
  title: "Your quote has expired",
  body: "Please remove it from your basket and start a new quote",
  type: "error",
};

const ALERT_REMOVEQUOTE_FAILED: TradeInError = {
  title: "Sorry, there's a problem removing your quote",
  body: "Please try again later",
  type: "error",
};

const ALERT_REMOVEQUOTE_SUCCESS: TradeInError = {
  title: "Quote removed from your basket:",
  body: "Description of device",
  type: "success",
};

const getAlertRemoveSuccess = (deviceDescription: string): TradeInError => {
  return {
    ...ALERT_REMOVEQUOTE_SUCCESS,
    body: deviceDescription,
  };
};

export const isExpired = (expiry: string) => {
  const currentDate = new Date();
  const expiryDate = new Date(expiry);
  return currentDate > expiryDate;
};

type Quote = {
  expiry: string;
};
const sortByExpiryDesc = (quoteA: Quote, quoteB: Quote) =>
  new Date(quoteA.expiry).getTime() - new Date(quoteB.expiry).getTime();

type Props = {
  quotes: QuotesResponse[];
  onClose: () => void;
  shouldValidateQuotes: boolean;
  onRemoveQuote: (id: string) => void;
  basketRemoveSuccess: boolean | null;
  allowRemove: boolean;
  lastDeletedQuote?: QuotesResponse;
  onStartNewQuote?: () => void;
};

const QuoteSummary = ({
  quotes,
  onClose,
  shouldValidateQuotes,
  onRemoveQuote,
  basketRemoveSuccess,
  lastDeletedQuote,
  onStartNewQuote,
  allowRemove,
}: Props) => {
  const [warning, setWarning] = useState<TradeInError>();
  const [removeId, setRemoveId] = useState<string>();

  const isMultipleQuotes = quotes.length > 1;
  const shouldStartNew = lastDeletedQuote && quotes.length === 0;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldValidateQuotes && basketRemoveSuccess === null) {
      if (quotes.length === 1) {
        // @ts-ignore
        if (isExpired(quotes[0].expiry)) {
          setWarning(ALERT_EXPIRED);
          dispatchTradeInQuoteExpired("Your quote has expired");
        } else {
          setWarning(ALERT_EXISTING);
          dispatchTradeInExistingQuote();
        }
      } else if (quotes.length > 1) {
        setWarning(ALERT_MULTIPLE);
        dispatchTradeInMultipleQuotes("one trade-in allowed per order");
      }
    }
    if (basketRemoveSuccess === true && lastDeletedQuote) {
      setWarning(getAlertRemoveSuccess(lastDeletedQuote?.deviceName));
      dispatchTradeInRemoveQuote();
    } else if (basketRemoveSuccess === false) {
      setWarning(ALERT_REMOVEQUOTE_FAILED);
    }
  }, [shouldValidateQuotes, quotes, basketRemoveSuccess, lastDeletedQuote]);

  useEffect(() => {
    containerRef.current?.focus();
  }, [containerRef]);

  const dispatchTradeInExistingQuote = () => {
    dispatchAnalyticsEvent(
      "global.interaction",
      {
        action: "error",
        type: "pdp",
        subType: "trade in summary - state - already have quote",
        additionalInfo: {},
        properties: {},
        products: [],
      },
      {},
    );
  };
  const dispatchTradeInQuoteExpired = (errorMessage: string) => {
    dispatchAnalyticsEvent(
      "global.interaction",
      {
        action: "error",
        type: "pdp",
        subType: "trade in item expired",
        additionalInfo: {},
        properties: {},
        products: [],
      },
      {
        error: {
          type: "trade in",
          message: errorMessage,
        },
      },
    );
  };

  const dispatchTradeInMultipleQuotes = (errorMessage: string) => {
    dispatchAnalyticsEvent(
      "global.interaction",
      {
        action: "error",
        type: "pdp",
        subType: "trade in multiple quotes",
        additionalInfo: {},
        properties: {},
        products: [],
      },
      {
        error: {
          type: "trade in",
          message: errorMessage,
        },
      },
    );
  };

  const dispatchTradeInRemoveQuote = () => {
    dispatchAnalyticsEvent(
      "global.interaction",
      {
        action: "global.interaction",
        type: "pdp",
        subType: "trade in remove from summary",
        additionalInfo: {},
        properties: {},
        products: [],
      },
      {},
    );
  };

  const handleRemoveQuote = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    onRemoveQuote(id);
    setRemoveId(id);
  };

  const sortedQuotes = [...quotes].sort(sortByExpiryDesc);

  return (
    <div className={styles.confirmationDetails} data-ref={containerRef}>
      <div className={styles.confirmationDetails__wrapper}>
        <h2 className={styles.confirmationDetails__heading}>
          {shouldValidateQuotes ? "Summary" : "Added to basket"}
        </h2>

        {warning && (
          <Message
            title={warning.title}
            body={warning.body}
            type={warning.type}
            className={styles.confirmationDetails__message}
          />
        )}

        {sortedQuotes.map(({ id, deviceName, expiry, payment }) => (
          <QuoteCard
            key={id}
            title="You are trading in:"
            payment={payment}
            deviceName={deviceName}
            expiry={expiry}
            instructions={
              !isExpired(expiry) && !shouldValidateQuotes
                ? paymentContent[payment.type].summaryNote
                : ""
            }
            ctaButton={
              !(isMultipleQuotes || isExpired(expiry)) ? (
                <>
                  <PrimaryButton
                    data-cy="continue-btn"
                    onClick={onClose}
                    submitting={id === removeId}
                  >
                    Continue shopping
                  </PrimaryButton>
                  {allowRemove && id !== removeId && (
                    <UnderlinedButton
                      className={styles.confirmationDetails__removeQuote}
                      onClick={(e: React.MouseEvent) =>
                        handleRemoveQuote(e, id)
                      }
                    >
                      Remove quote
                    </UnderlinedButton>
                  )}
                </>
              ) : (
                <PrimaryButton
                  className={styles.confirmationDetails__removeQuotePrimary}
                  onClick={(e: React.MouseEvent) => handleRemoveQuote(e, id)}
                  submitting={id === removeId}
                >
                  Remove quote
                </PrimaryButton>
              )
            }
            removeIconButton={
              allowRemove &&
              id !== removeId && (
                <Close24PxOutlinedIcon
                  className={styles.confirmationDetails__removeQuoteIcon}
                  onClick={(e: React.MouseEvent) => handleRemoveQuote(e, id)}
                />
              )
            }
          />
        ))}

        {shouldStartNew && (
          <div className={styles.confirmationDetails__startNewCta}>
            <h2 className={styles.confirmationDetails__heading}>
              Need a new quote?
            </h2>
            <PrimaryButton
              className={styles.confirmationDetails__newQuoteBtn}
              onClick={onStartNewQuote}
            >
              Start a new quote
            </PrimaryButton>
          </div>
        )}
      </div>
      <div className={styles.confirmationDetails__footer}>
        {!isMultipleQuotes && !shouldValidateQuotes ? (
          <NextStep step={TradeInSteps.CHECKOUT} />
        ) : null}
        {shouldStartNew ? <NextStep step={TradeInSteps.START} /> : null}
      </div>
    </div>
  );
};

export default QuoteSummary;
